import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router";

import * as Api from "@/api";
import FabBar from "@/design/components/FabBar";
import MasterListVirtualized from "@/design/components/MasterListVirtualized";
import { disableSearch, enableSearch } from "@/store/reducers/ui";
import useSearch from "@/utils/hooks/useSearch";
import { toggleLoadingState } from "@/utils/utility";
import Valkyrie, { viPlus, viVoicemail } from "@sippy-platform/valkyrie";

import { useMediaQuery, useTheme } from "@mui/material";
import Button from "@mui/material/Button";

function VoicemailList() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const matchesMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const voicemailStore = useSelector((state) => state.domain.voicemails);
  const searchValue = useSelector((state) => state.ui.searchValue);
  const progressBar = useSelector((state) => state.ui.progressBar);

  // Search every time voicemailList or searchQuery changes
  const searchResults = useSearch(voicemailStore.list, searchValue, ["name", "exten"]);

  useEffect(() => {
    toggleLoadingState(true);
    Promise.resolve(Api.getVoicemails()).finally(toggleLoadingState);
  }, []);

  // Open first item or new if the viewport is large enough
  useEffect(() => {
    if (!id && matchesMdUp && voicemailStore.meta.lastFetched) {
      if (voicemailStore.list.length >= 1) {
        navigate(`${voicemailStore.list[0].id}`);
      } else {
        navigate("new");
      }
    }
  }, [id, matchesMdUp, navigate, voicemailStore.list, voicemailStore.meta.lastFetched]);

  useEffect(() => {
    dispatch(enableSearch());
  }, [dispatch]);
  useEffect(() => () => dispatch(disableSearch()), [dispatch]);

  return (
    <>
      <MasterListVirtualized
        fabBar
        title="Voicemails"
        aria-label="Voicemail list"
        list={searchResults}
        listItemProps={{
          primary: (x) => x.name,
          secondary: (x) => x.exten,
          to: (x) => `../${x.id}`,
          selected: (x) => x.id === Number(id),
          icon: viVoicemail,
        }}
        hideContentLoader={voicemailStore.meta.count >= 1 && searchResults.length >= 1}
        contentLoaderProps={{
          count: voicemailStore.meta.count,
          loading: progressBar,
          noItems: "You don't have any voicemails yet",
          noResults: "No voicemails in your current query",
          fetching: "Getting your voicemails",
          icon: viVoicemail,
        }}
      />
      <FabBar>
        <Button variant="contained" component={NavLink} to="new" startIcon={<Valkyrie icon={viPlus} />}>
          Add
        </Button>
      </FabBar>
    </>
  );
}

export default VoicemailList;
